import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { csv } from 'd3-fetch'
import { useChartDimensions } from '../../hooks/dimensions'
import { Flex } from '../atoms/Basics'
import ChartTitle from '../atoms/ChartTitle'
import ChartContainer from '../atoms/ChartContainer'
import SvgWrapper from '../molecules/SvgWrapper'
import Sankey from '../molecules/Sankey'
import Note from '../atoms/Note'
import YearSelector from '../atoms/yearSelector'

const ComparisonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2rem;

  @media (min-width: 1200px) {
    flex-direction: row;
    gap: 1rem;
  }
`

const ChartWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

const SankeyContainer = styled.div`
  width: 100%;
  height: 625px;
  margin: 2rem 0;
`

const LegendCircle = styled.div`
  background-color: ${(props) => props.theme.colors.blue};
  border-radius: 50%;
  width: 1.75rem;
  min-width: 1.75rem;
  height: 1.75rem;
  margin-right: 0.5rem;
`

const SelectorRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const SinglePerfVsPot = ({ selectedYear, availableYears, onYearChange }) => {
  const [preppedData, setPreppedData] = useState()
  const [allData, setAllData] = useState(null)
  const [ref, dimensions] = useChartDimensions()

  useEffect(() => {
    let isMounted = true
    csv(process.env.PUBLIC_URL + '/data/perfVsPot.csv').then((data) => {
      if (isMounted) {
        setAllData(data)
      }
    })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (allData && selectedYear) {
      const filteredData = allData.filter((row) => row.year === selectedYear)
      setPreppedData(prepData(filteredData))
    }
  }, [allData, selectedYear])

  const prepData = (data) => {
    // Filter out rows with NA values in rankings
    const filteredData = data.filter(
      (row) =>
        row.current_overall_performance_ranking !== 'NA' &&
        row.current_overall_performance_ranking !== '' &&
        row.capacity_to_advance_ranking !== 'NA' &&
        row.capacity_to_advance_ranking !== ''
    )

    const dataSorted = filteredData.sort((a, b) =>
      +a.current_overall_performance_ranking <
      +b.current_overall_performance_ranking
        ? -1
        : 1
    )

    const links = dataSorted?.map((l) => ({
      source: l.group,
      target: 'tg' + l.group,
      value: 1,
      leftRank: +l.current_overall_performance_ranking,
      rightRank: +l.capacity_to_advance_ranking
    }))

    const nodes = [
      ...new Set(
        links
          ? links
              .map((l) => [
                {
                  name: l.source,
                  category: l.source,
                  leftRank: l.leftRank,
                  rightRank: 0
                },
                {
                  name: l.source,
                  category: l.target,
                  leftRank: 0,
                  rightRank: l.rightRank
                }
              ])
              .flat()
          : null
      )
    ]

    const leftNodes = nodes.filter((node) => node.leftRank)
    const rightNodes = nodes.filter((node) => node.rightRank)

    leftNodes.sort((a, b) => (a.leftRank < b.leftRank ? -1 : 1))
    rightNodes.sort((a, b) => (a.rightRank < b.rightRank ? -1 : 1))

    const sortedNodes = rightNodes.concat(leftNodes)
    return { n: sortedNodes, l: links }
  }

  return (
    <ChartWrapper>
      <SelectorRow>
        <YearSelector value={selectedYear} onChange={onYearChange}>
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearSelector>
      </SelectorRow>
      <SankeyContainer ref={ref}>
        <SvgWrapper dimensions={dimensions}>
          <Sankey data={preppedData} />
        </SvgWrapper>
      </SankeyContainer>
    </ChartWrapper>
  )
}

const PerfVsPotComparison = ({ note }) => {
  const $ = window.WILDLABS_VARS
  const [availableYears, setAvailableYears] = useState([])
  const [leftYear, setLeftYear] = useState('2020')
  const [rightYear, setRightYear] = useState('2024')

  useEffect(() => {
    let isMounted = true
    csv(process.env.PUBLIC_URL + '/data/perfVsPot.csv').then((data) => {
      if (isMounted) {
        // Extract unique years from the data
        const years = [...new Set(data.map((row) => row.year))].sort((a, b) => {
          if (a === 'All Years') return -1
          if (b === 'All Years') return 1
          return a.localeCompare(b)
        })
        setAvailableYears(years)
      }
    })

    return () => {
      isMounted = false
    }
  }, [])

  const handleLeftYearChange = (event) => {
    setLeftYear(event.target.value)
  }

  const handleRightYearChange = (event) => {
    setRightYear(event.target.value)
  }

  return (
    <ChartContainer widthIndex={2}>
      <ChartTitle text={$.perf_titles.chartTitle + ' - Year Comparison'} />

      <Flex>
        <LegendCircle />
        <p>= perceived as having the highest untapped potential</p>
      </Flex>

      <ComparisonContainer>
        <SinglePerfVsPot
          selectedYear={leftYear}
          availableYears={availableYears}
          onYearChange={handleLeftYearChange}
        />

        <SinglePerfVsPot
          selectedYear={rightYear}
          availableYears={availableYears}
          onYearChange={handleRightYearChange}
        />
      </ComparisonContainer>

      <Note note={note} type={'sankey'} />
    </ChartContainer>
  )
}

export default PerfVsPotComparison
